/**
 * "Ready to go online?", turned into the rows the design draws, away from any rendering.
 *
 * Three designed screens over one read of EXP-003's `shift-readiness`:
 *
 *   - `1 Go online` — _"Ready to go online?"_ / _"All four must pass before you enter the dispatch
 *     pool."_
 *   - `1b Can't go online` — _"Your PrDP has expired"_ / _"You can't go online until a valid permit
 *     is approved."_
 *   - `7 Document expiring` — _"Finish your jobs, then renew"_ / _"At 23:59 you'll go offline
 *     automatically. Current jobs can finish."_
 *
 * The judgement itself is **not** here. It is in EXP-003, which is the only place six services'
 * vocabularies are joined, and this file reads its answer rather than re-deriving one — because two
 * places deciding "an expired permit blocks going online" is one place getting it wrong, in the
 * direction of letting somebody work.
 *
 * ## The rule this file must not break
 *
 * `readyToGoOnline` has three values. A gate nobody could answer leaves it `unknown`, never `pass`.
 * R-09 and R-18 are both records of a screen drawing an unconfirmable state as a confirmed one, and
 * the consequence here is a courier told they may go online when nothing checked their licence. So
 * `verdictToStatus` maps `unknown` to the design's `wait` row and never to its `ok` row, and the
 * screen's chip for an `unknown` aggregate is the design's dashed `unknown` pill carrying a literal
 * `?`.
 *
 * ## Four rows, five gates
 *
 * The design draws four check rows on screen 1 and EXP-003 answers five gates. That is not a
 * mismatch to paper over:
 *
 *   - `Documents valid` ← the `documents` gate.
 *   - `Vehicle approved` ← the `vehicle` gate.
 *   - `Inside the service area` ← the `serviceArea` gate.
 *   - `Location always on` ← **the device's own geolocation permission**, which is the one row on
 *     this screen that no service can answer and the application genuinely owns.
 *
 * The `profile` and `presence` gates have **no drawn row**, and they still decide the headline: a
 * suspended profile or a lapsed availability lease blocks a courier the design would have drawn four
 * green ticks for. Rather than invent a fifth and sixth row, they are reported in the sub-line of
 * the screen that owns the verdict. Recorded as R-29 and Q-61.
 */

import type { Gate } from "@tdelivr/api-client/generated/experience/driver-bff.js";

/** EXP-003's `shift-readiness` response, as the generated client types it. */
export interface ReadinessView {
  readonly tenantId: string;
  readonly driverId: string;
  readonly observedAt: string;
  readonly displayName: string | null;
  readonly readyToGoOnline: "pass" | "blocked" | "unknown";
  readonly gates: readonly Gate[];
  readonly documents: readonly {
    readonly documentId: string;
    readonly type: string;
    readonly verificationStatus: "pending" | "approved" | "rejected";
    readonly expiringAt: string | null;
    readonly expiringSoon: boolean;
    readonly expired: boolean;
    readonly latestDecisionReason: string | null;
  }[];
  readonly countsAreTotals: boolean;
  readonly countFloors: readonly {
    readonly layer: string;
    readonly service: string;
    readonly reason: "truncated" | "did_not_answer" | "bounded";
  }[];
  readonly documentDecisionLookups: number;
  readonly sources: readonly {
    readonly layer: string;
    readonly service: string;
    readonly status: "ok" | "forbidden" | "unauthorized" | "unavailable" | "timeout" | "invalid";
    readonly scopes: readonly string[];
    readonly code?: string;
    readonly truncated?: boolean;
  }[];
  readonly degraded: boolean;
}

/** Which of the three readiness screens the answer puts a courier on. */
export type ReadinessScreen = "go-online" | "cannot-go-online" | "document-expiring";

/** The four statuses a check row can take, from `15 WP14`'s own superset. */
export type CheckStatus = "ok" | "bad" | "warn" | "wait";

/** Whether the browser will give this page a position, which is the fourth row's whole subject. */
export type LocationPermission = "granted" | "denied" | "prompt" | "unsupported" | "unknown";

/** One row of the design's check list. */
export interface CheckRow {
  /** Stable, so a test and a comparison can name a row rather than count them. */
  readonly key: "documents" | "vehicle" | "serviceArea" | "location";
  readonly status: CheckStatus;
  /** The design's glyph for this status, from `11`'s and `15`'s own mapping. */
  readonly glyph: "check_circle" | "error" | "warning" | "pending";
  /** Word for word from the design. Never derived. */
  readonly title: string;
  /** The evidence. Data where the platform publishes it, the named absence where it does not. */
  readonly sub: string;
  /** True when `sub` is a recorded absence rather than a value, so a screen can mark it. */
  readonly subIsAbsence: boolean;
}

/**
 * A gate's verdict as a row's status.
 *
 * The one mapping that matters: `unknown` becomes `wait` and never `ok`. Four green ticks over a
 * gate nobody checked is the R-09 over-claim with a courier's licence as its subject.
 */
export function verdictToStatus(verdict: Gate["verdict"]): CheckStatus {
  return verdict === "pass" ? "ok" : verdict === "blocked" ? "bad" : "wait";
}

/** The glyph each status carries, read from the design's own ternary in `15 WP14`. */
export function glyphFor(status: CheckStatus): CheckRow["glyph"] {
  return status === "ok"
    ? "check_circle"
    : status === "bad"
      ? "error"
      : status === "warn"
        ? "warning"
        : "pending";
}

function gateOf(view: ReadinessView, name: Gate["gate"]): Gate | undefined {
  return view.gates.find((gate) => gate.gate === name);
}

/**
 * Which screen a readiness answer puts the courier on, in the order the design's own flow implies.
 *
 * `1b` wins over `7` and over `1`: a courier whose permit expired yesterday is not shown a warning
 * that one expires tonight, and is not shown a Go online button that would be refused. `7` wins over
 * `1` because its whole purpose is to be seen while the courier is still allowed to work — the
 * design's words are _"Current jobs can finish."_
 *
 * A `blocked` verdict that is **not** about a document still goes to `1b`. That screen is the only
 * one of the three that draws a refusal, and a suspended profile or a withheld availability is a
 * refusal; its headline then names what actually blocked rather than the permit. The alternative —
 * drawing screen 1 with a Go online button over a `blocked` verdict — is a button that fails.
 */
export function screenFor(view: ReadinessView): ReadinessScreen {
  if (view.readyToGoOnline === "blocked" && !onlyBlockedByBeingOffline(view)) {
    return "cannot-go-online";
  }
  return view.documents.some((document) => document.expiringSoon)
    ? "document-expiring"
    : "go-online";
}

/**
 * Whether the only thing blocking this courier is that they are not online yet.
 *
 * **Without this, screen 1 is unreachable.** EXP-003's presence gate returns `blocked` with
 * `presence_offline` whenever DRV-05 says a courier is offline, and `overallVerdict` makes one
 * `blocked` gate a `blocked` aggregate — so a courier who is offline, which is every courier
 * looking at `Ready to go online?`, would be routed to `1b Can't go online` and shown
 * `Upload renewed permit` over a perfectly valid permit.
 *
 * This is not a reading against EXP-003. Its own gate says so in as many words: _"`presence:
 * "offline"` is a state rather than a fault, and the first screen's whole purpose is to be looked at
 * from it, so it blocks with its own reason rather than failing."_ The gate blocks so that nothing
 * **else** can read the aggregate as dispatchable; the screen whose subject is going online has to
 * read the reason rather than the verdict.
 *
 * Narrow on purpose, and the narrowness is the safety. `presence_stale` is not this — a lapsed lease
 * is a record that is a memory rather than a report, and R-09 is the record of a console drawing
 * exactly that case as working. Neither is `not_dispatchable`, which is DRV-05 withholding a courier
 * from supply for a reason of its own. Only `presence_offline`, only on the presence gate, and only
 * when no other gate is blocked.
 *
 * Recorded as R-29.
 */
export function onlyBlockedByBeingOffline(view: ReadinessView): boolean {
  const blocked = view.gates.filter((gate) => gate.verdict === "blocked");
  return (
    blocked.length === 1 &&
    blocked[0]!.gate === "presence" &&
    blocked[0]!.reason === "presence_offline"
  );
}

/* ------------------------------------------------------------------------------- the check rows */

/**
 * `Documents valid` — the design's title, with `PrDP, licence and insurance · next expiry in 23
 * days` as its sub-line.
 *
 * Both halves of that sub-line are published. The types are DRV-03's own `type` values, listed in the
 * order DRV-03 answered because the contract publishes no ordering for that list and inventing one
 * would be deciding which of a courier's papers matters most. The `next expiry in N days` is the
 * nearest future `expiringAt` against `observedAt` — the aggregate's own instant, not the phone's,
 * because the expiry was evaluated against that instant and a day's drift between two clocks would
 * put a row on the wrong side of midnight.
 */
export function documentsRow(view: ReadinessView): CheckRow {
  const gate = gateOf(view, "documents");
  const status = gate === undefined ? "wait" : verdictToStatus(gate.verdict);
  return {
    key: "documents",
    status,
    glyph: glyphFor(status),
    ...documentsSub(view, gate),
    title: "Documents valid",
  };
}

function documentsSub(
  view: ReadinessView,
  gate: Gate | undefined,
): { readonly sub: string; readonly subIsAbsence: boolean } {
  if (gate === undefined || gate.verdict === "unknown") {
    return { sub: absenceFor(gate), subIsAbsence: true };
  }
  if (gate.verdict === "blocked") {
    /*
     * The one line on this screen the design writes twice, and the two wordings differ.
     *
     * Screen 1b's headline is `Your PrDP has expired` and its row's sub-line is
     * `Upload the renewed permit to continue`. The row keeps the design's words; what the platform
     * supplies is which document, which is EXP-003's `upstreamReason` — the document's own `type`
     * for an expiry, and DRV-03's own decision `reasonCode` for a rejection.
     */
    const expired = view.documents.find((document) => document.expired);
    if (expired !== undefined) {
      return { sub: "Upload the renewed permit to continue", subIsAbsence: false };
    }
    const unapproved = view.documents.find(
      (document) => document.verificationStatus !== "approved",
    );
    if (unapproved === undefined) {
      // `no_documents`: DRV-03 answered and there are none. Blocked rather than passing, and said so.
      return { sub: "Nothing uploaded yet", subIsAbsence: false };
    }
    return {
      sub:
        unapproved.latestDecisionReason === null
          ? `${unapproved.type} · ${unapproved.verificationStatus}`
          : `${unapproved.type} · ${unapproved.latestDecisionReason}`,
      subIsAbsence: false,
    };
  }

  const types = [...new Set(view.documents.map((document) => document.type))];
  const nextExpiry = nextExpiryDays(view);
  const kinds = listOf(types);
  if (nextExpiry === undefined) return { sub: kinds, subIsAbsence: false };
  return { sub: `${kinds} · next expiry in ${nextExpiry} days`, subIsAbsence: false };
}

/**
 * `PrDP, licence and insurance` — the design's own form for this list, not a comma-separated one.
 *
 * The values are DRV-03's and the punctuation between them is the design's, which writes
 * `PrDP, licence and insurance · next expiry in 23 days`. Written as a comma-joined list first, and
 * the acceptance check found it: the shorter line stopped wrapping to two lines at 360dp, every
 * check row below it moved up seventeen pixels, and the screen differed from its design by 3.29% of
 * its pixels for a reason that was one word.
 *
 * The order is DRV-03's own, because the contract publishes no ordering for that list and choosing
 * one would be deciding which of a courier's papers matters most.
 */
export function listOf(values: readonly string[]): string {
  if (values.length === 0) return "";
  if (values.length === 1) return values[0]!;
  return `${values.slice(0, -1).join(", ")} and ${values.at(-1)!}`;
}

/**
 * Whole days until the nearest future expiry, against the aggregate's own instant.
 *
 * **`Math.floor`, and the direction is the point.** A permit that lapses in 23 days and four hours
 * has 23 whole days of use left, not 24: it dies partway through the twenty-fourth. Rounding up
 * would tell a courier they had a day they do not have, which on a permit is a courier planning a
 * shift they will be taken offline during.
 *
 * This was written as `Math.ceil` first, with that same reasoning in the comment above it arguing
 * for the opposite — caught by `tests/shift-readiness.test.ts`, which is the only reason it is right
 * now. A comment and its code disagreeing is exactly the kind of thing only a test notices.
 */
export function nextExpiryDays(view: ReadinessView): number | undefined {
  const observedAt = Date.parse(view.observedAt);
  if (!Number.isFinite(observedAt)) return undefined;
  const upcoming = view.documents
    .map((document) => (document.expiringAt === null ? NaN : Date.parse(document.expiringAt)))
    .filter((instant) => Number.isFinite(instant) && instant > observedAt);
  if (upcoming.length === 0) return undefined;
  return Math.floor((Math.min(...upcoming) - observedAt) / 86_400_000);
}

/**
 * `Vehicle approved` — the design's title, with `Motorcycle T 482 DKX` as its sub-line.
 *
 * Half of that sub-line was called the structural absence of this screen, on the grounds that
 * **nothing on this platform joins a courier to a vehicle**. It does: DRV-04 publishes
 * `GET /v1/drivers/{driverId}/vehicle-assignments`, this application has been calling it for the
 * Vehicle screen all along, and `GoOnline` now resolves the identifier from there before asking
 * EXP-003 for readiness. The claim was true when written and outlived its subject.
 *
 * What **is** still absent is the sub-line's words. The gate publishes a verdict and not the
 * registration and class the design prints, so `Motorcycle T 482 DKX` has no source even with an
 * assignment resolved: the vehicle's own read carries the plate, and the gate does not pass it
 * through. So the row draws `Approved` on a pass and the absence otherwise, and the design's
 * sub-line remains undrawable. The earlier note that `nothing publishes driverProfile.vehicleId
 * or vehicle.driverId or availability.vehicleId` is literally true about those three fields and
 * was misleading about the question, because the assignment resource publishes the join.
 */
export function vehicleRow(view: ReadinessView): CheckRow {
  const gate = gateOf(view, "vehicle");
  const status = gate === undefined ? "wait" : verdictToStatus(gate.verdict);
  return {
    key: "vehicle",
    status,
    glyph: glyphFor(status),
    title: "Vehicle approved",
    sub: gate?.verdict === "pass" ? "Approved" : absenceFor(gate),
    subIsAbsence: gate?.verdict !== "pass",
  };
}

/**
 * `Inside the service area` — the design's title, with `Masaki, Dar es Salaam` as its sub-line.
 *
 * This one is published, conditionally and only on the passing path: GEO-06 answers with an
 * `areaName`, and EXP-003 carries it through as the gate's `upstreamReason`. Without a `productRef`
 * — which no driver record publishes — the gate is `unknown` and there is no area to name.
 */
export function serviceAreaRow(view: ReadinessView): CheckRow {
  const gate = gateOf(view, "serviceArea");
  const status = gate === undefined ? "wait" : verdictToStatus(gate.verdict);
  const named = gate?.verdict === "pass" ? gate.upstreamReason : undefined;
  return {
    key: "serviceArea",
    status,
    glyph: glyphFor(status),
    title: "Inside the service area",
    sub: named ?? (gate?.verdict === "pass" ? "Inside a serving area" : absenceFor(gate)),
    subIsAbsence: named === undefined && gate?.verdict !== "pass",
  };
}

/**
 * `Location always on` — the design's title, with `Needed while you're online` as its sub-line.
 *
 * The only row on this screen that is not the platform's. The browser's Permissions API answers it,
 * and where it does not — Safari has never implemented `permissions.query` for geolocation — the
 * answer is `unsupported`, which is a `wait` row rather than a tick. The sub-line is the design's
 * word for word on the paths where it holds, and says what is wrong where it does not.
 */
export function locationRow(permission: LocationPermission): CheckRow {
  const status: CheckStatus =
    permission === "granted"
      ? "ok"
      : permission === "denied"
        ? "bad"
        : permission === "prompt"
          ? "warn"
          : "wait";
  return {
    key: "location",
    status,
    glyph: glyphFor(status),
    title: "Location always on",
    sub:
      permission === "granted"
        ? "Needed while you're online"
        : permission === "denied"
          ? "Turn location on for this app to go online"
          : permission === "prompt"
            ? "Needed while you're online"
            : "This browser will not say whether location is on",
    subIsAbsence: permission === "unsupported" || permission === "unknown",
  };
}

/**
 * The four rows, in the design's own order.
 *
 * Screen 1 draws documents, vehicle, service area, location. Screen 1b draws three — the failing
 * document, the vehicle and the service area — and omits the location row, so that screen takes the
 * first three of these. The order is the design's and is not alphabetical, derived or sorted by
 * severity: it is the order a courier reads down.
 */
export function checkRows(view: ReadinessView, permission: LocationPermission): CheckRow[] {
  return [documentsRow(view), vehicleRow(view), serviceAreaRow(view), locationRow(permission)];
}

/**
 * Why a gate could not be answered, in words, as the row's sub-line.
 *
 * Codes rather than sentences is EXP-003's own rule — _"the design owns the words"_ — so the words
 * are chosen here. Each is the named reason rather than a soothing generality, because a courier
 * sent to check their permit when the problem is that DRV-04 did not answer has been sent to the
 * wrong place. The copy is this build's: the design draws no row for an unconfirmable gate at all.
 * Recorded as Q-61.
 */
export function absenceFor(gate: Gate | undefined): string {
  if (gate === undefined) return "Not checked";
  /*
   * `undefined` is answered before the switch rather than as a case in it.
   *
   * A gate with no `reason` is a gate EXP-003 answered `pass` for — its own rule is "present
   * whenever the verdict is not `pass`, absent when it is" — so reaching here with none is a row
   * whose own verdict was not `pass` and which gave no reason, which is a contract defect rather
   * than a state. "Could not be checked" is the honest thing to say about it. Taken out of the
   * switch so that the switch is over the sixteen published reasons alone and the `never` at its
   * foot is a real exhaustiveness check: a seventeenth reason added to the contract fails the build.
   */
  if (gate.reason === undefined) return "Could not be checked";
  switch (gate.reason) {
    case "vehicle_not_joinable":
      return "Nothing links you to a vehicle yet";
    case "vehicle_absent":
      return "That vehicle is not on record";
    case "vehicle_not_active":
      return `Not active · ${gate.upstreamReason ?? "status not given"}`;
    case "product_not_named":
      return "No service to check this against";
    case "position_absent":
      return "No recent location for you";
    case "outside_service_area":
      return "You are outside the serving area";
    case "document_expired":
      return "Upload the renewed permit to continue";
    case "document_unapproved":
      return gate.upstreamReason ?? "Still being checked";
    case "no_documents":
      return "Nothing uploaded yet";
    case "profile_absent":
      return "No courier record for you in this tenant";
    case "profile_not_active":
      return `Your record is ${gate.upstreamReason ?? "not active"}`;
    case "presence_offline":
      return "You are offline";
    case "presence_stale":
      return "Your phone stopped reporting, so this is a memory";
    case "not_dispatchable":
      return gate.upstreamReason ?? "Held back from dispatch";
    case "forbidden":
      return "You do not have permission to check this";
    case "source_unavailable":
      return "Could not be checked";
    default:
      return unreachable(gate.reason);
  }
}

/** A reason the contract added and this build has no words for. Fails the build, not the courier. */
function unreachable(reason: never): never {
  throw new Error(`no wording for gate reason ${String(reason)}`);
}

/* ------------------------------------------------------------------- the headline and the chip */

/** The design's seven chip classes, by name. */
export type ChipClass =
  "idle" | "human" | "done" | "closed" | "active" | "unknown" | "caution" | "waiting";

/**
 * The chip on a readiness screen: its class, and the word beside its glyph.
 *
 * The design's own words, one per screen: `offline` on screen 1, `action_required` on 1b,
 * `expires tonight` on 7. The one addition is the `unknown` class for an aggregate nothing could
 * confirm, which the design supplies the pill for — dashed, mono, carrying a `?` — and supplies no
 * word for. `unconfirmed` is this build's, recorded in Q-61, and it is the one word on this screen
 * that must not be mistaken for either of the other two.
 */
export function readinessChip(view: ReadinessView): { class: ChipClass; label: string } {
  if (view.readyToGoOnline === "blocked" && !onlyBlockedByBeingOffline(view)) {
    return { class: "human", label: "action_required" };
  }
  if (view.readyToGoOnline === "unknown") return { class: "unknown", label: "unconfirmed" };
  return view.documents.some((document) => document.expiringSoon)
    ? { class: "caution", label: "expires tonight" }
    : { class: "idle", label: "offline" };
}

/**
 * The document the warning screens are about: the soonest one that expires inside the window.
 *
 * `expiringSoon` is EXP-003's own flag, derived from DRV-03's `expiresOn` against a configured
 * warning window rather than from anything DRV-03 publishes as a warning — nothing does. The
 * soonest wins because screen 7's headline names one document and one hour.
 */
export function expiringDocument(
  view: ReadinessView,
): ReadinessView["documents"][number] | undefined {
  return view.documents
    .filter((document) => document.expiringSoon && document.expiringAt !== null)
    .sort((left, right) => Date.parse(left.expiringAt!) - Date.parse(right.expiringAt!))[0];
}

/** The document that blocked, for screen 1b's headline. Expired first, then unapproved. */
export function blockingDocument(
  view: ReadinessView,
): ReadinessView["documents"][number] | undefined {
  return (
    view.documents.find((document) => document.expired) ??
    view.documents.find((document) => document.verificationStatus !== "approved")
  );
}
